import React, { useEffect, useState, useRef, useMemo } from 'react';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import MediaQuery from 'react-responsive';
import ReactStickyBox from 'react-sticky-box';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import '../../Styles/donateNow.css';
import '../../scss/fundraiser-list.scss';
import useForm from '../../hooks/UseForm';
import { deviceDetect } from 'react-device-detect';
import { validateCheckout } from '../../validations';
import { history, scrollToTop } from '../../helpers';
import Tracking from '../../services/tracking.service';
import { GTM_EVENTS, S3_BUCKET } from '../../constants';
import { RECAPTCHA_SCRIPT_URL, WEB_URL } from '../../api/URLs';
import { useNonInitialEffect } from '../../hooks/UseNonInitialEffect';
import { DonationImpactCard, Footer, Header, MixPanelTrackedLink } from '../../components';
import { StyledCheckoutForm } from './checkoutComponents/CheckoutStyles';
import { HBLForm } from './checkoutComponents/paymentOptionComponents//HBL';
import { SET_ACH_REF, SET_GOOGLE_APPLE_REF, SET_STRIPE_REF } from '../../store/actionTypes';
import { checkoutActions, donationActions, fundRaiserPageAction, userActions } from '../../store/actions';
import { GiftAid, CorporateMatch, ContactDetailsSection, PaymentsSection, AmountSection, DonateNowButton, KeepInTouch } from './checkoutComponents';
import { redirect, useNavigate } from 'react-router-dom';
import { collectSourceURL } from '../../services/source.tracking.service';

export default function ({ isFundraiserCheckout = true, pageDetails }) {
	// const hblReference = useRef();

	const ACHReference = useRef();
	const stripeReference = useRef();
	const googleApplePayReference = useRef();

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const errors = useSelector((state) => state.validationErrors);
	const authentication = useSelector((state) => state.authentication);
	const partnerLogo = useSelector((state) => state.donation.partnerLogo);
	const userCurrentLocation = useSelector((state) => state.fundraiser_page.location_details);
	const taxesMessage = useSelector((state) => state.donation.checkoutMessages.taxes_message);
	const countryHasGiftAid = useSelector((state) => state.check_if_country_has_giftaid.if_giftaid);
	const countryAllowedForCorporateMatch = useSelector((state) => state.isCountryCorporateMatch.if_corporate_match);
	const checkoutState = useSelector((state) => state.donationCheckout);

	const [startTime, setStartTime] = useState(null);

	const handleStateChange = (event) => {
		dispatch(checkoutActions.updateCheckoutState(event));
	};

	const processDonation = async (googleApplePaymentId = '') => {
		window.grecaptcha.ready(async function () {
			dispatch(checkoutActions.setGoogleApplePayId(googleApplePaymentId));

			dispatch(checkoutActions.setPaymentRef(SET_ACH_REF, ACHReference));
			dispatch(checkoutActions.setPaymentRef(SET_STRIPE_REF, stripeReference));
			dispatch(checkoutActions.setPaymentRef(SET_GOOGLE_APPLE_REF, googleApplePayReference));

			dispatch(checkoutActions.initiateDonationProcess(startTime));
		});
	};
	const { handleChange, handleSubmit } = useForm(processDonation, validateCheckout, handleStateChange, checkoutState);

	const mapCurrencyBasedComponent = useMemo(() => {
		if (countryHasGiftAid)
			//GiftAid amount is 25% of the donation amount paid by the government of
			//selected country, if the selected country is eligible for giftaid.
			return <GiftAid handleChange={handleChange} validationErrors={errors.giftAidBilling} />;
		else if (countryAllowedForCorporateMatch) return <CorporateMatch validationErrors={errors} handleChange={handleChange} />;

		return null;
	}, [
		countryHasGiftAid,
		countryAllowedForCorporateMatch,
		checkoutState.makeGiftAidDonation,
		checkoutState.sameBillingForGiftAid,
		checkoutState.donationAmount,
		errors.giftAidBilling,
		errors.corporateMatch,
		checkoutState.giftAidBilling,
		checkoutState.corporateMatchEmployer,
		checkoutState.doubleTheImpact,
		// checkoutState.contactMeForCorporateMatch
	]);

	useEffect(() => {
		setStartTime(new Date());

		scrollToTop();
		console.log('Device property: ', deviceDetect());

		dispatch(fundRaiserPageAction.getUserDefaultCurrency());

		if (authentication.user) dispatch(donationActions.getUserDetails(authentication.user.id));
	}, []);

	useNonInitialEffect(() => {
		// Verifying if user's current location country is active by admin panel
		dispatch(donationActions.checkIfCountryPartnerIsactiveDonor(userCurrentLocation.country_id));
	}, [userCurrentLocation]);

	useNonInitialEffect(() => {
		if (!isFundraiserCheckout && !pageDetails.allow_direct_donations) {
			navigate(WEB_URL.PAGE_NOT_FOUND);
		} else
			Tracking.trackGTM({
				event: GTM_EVENTS.PAYMENT_INITIATED,
				fundrasier_name: pageDetails.page_title,
				raised_amount: pageDetails.total_fund_raised,
				number_of_donation: pageDetails.count_donations,
				goal_amount: pageDetails.fundraising_goal,
			});

		dispatch(donationActions.checkIfCountryPartnerIsactiveFundraiser(pageDetails.country_id));
	}, [pageDetails]);

	useEffect(() => {
		if (checkoutState.userContactDetails.country && checkoutState.selectedCountry.id)
			dispatch(userActions.checkAllowedCountryForCheque(checkoutState.userContactDetails.country, checkoutState.selectedCountry.id));
	}, [checkoutState.selectedCountry.id, checkoutState.userContactDetails.country]);

	useEffect(() => {
		dispatch(
			checkoutActions.updateCheckoutState({
				target: {
					name: 'isFundraiserCheckout',
					value: isFundraiserCheckout,
				},
			}),
		);
	}, [isFundraiserCheckout]);

	useEffect(() => {
		const script = document.createElement('script');

		script.src = RECAPTCHA_SCRIPT_URL;
		script.async = true;

		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, []);

	return (
		<React.Fragment>
			<Header />
			<StyledCheckoutForm className="App-content">
				<Grid className="OuterGrid" container spacing={2}>
					<Grid item xs={12} md={8}>
						<Stack spacing={5}>
							{pageDetails && (
								<AmountSection
									isFundraiserCheckout={isFundraiserCheckout}
									handleChange={handleChange}
									validationErrors={errors}
									pageDetails={pageDetails}
								/>
							)}
							{/* Donation impact card will display before user contact details on mobile screen */}
							<MediaQuery maxDeviceWidth={992}>
								{pageDetails ? (
									<DonationImpactCard pageDetails={pageDetails} assignClasses="whitebox" isFundraiserPage={isFundraiserCheckout} />
								) : null}
							</MediaQuery>
							<ContactDetailsSection handleChange={handleChange} validationErrors={errors} pageDetails={pageDetails} />
							<PaymentsSection ACHReference={ACHReference} stripeReference={stripeReference} handleChange={handleChange} validationErrors={errors} />
							{mapCurrencyBasedComponent}
							<KeepInTouch handleCheck={handleChange} />
							<DonateNowButton googleApplePayReference={googleApplePayReference} handleClick={handleSubmit} />
							{partnerLogo ? <img style={{ width: '150px' }} src={S3_BUCKET + partnerLogo} /> : null}
							{taxesMessage ? <Typography variant="small">{taxesMessage}</Typography> : null}
							<Typography align="center" variant="small" color="neutral.dark">
								<i>
									This site is protected by reCAPTCHA and the Google &nbsp;
									<MixPanelTrackedLink
										type="link"
										label="Privacy Policy"
										context="Google recaptcha privacy policy"
										href="https://policies.google.com/privacy"
										color="neutral.dark"
									>
										<i>Privacy Policy</i>
									</MixPanelTrackedLink>{' '}
									and &nbsp;
									<MixPanelTrackedLink
										type="link"
										label="Terms of Service apply."
										context="Google recaptcha Terms of Service apply."
										href="https://policies.google.com/terms"
										color="neutral.dark"
									>
										<i>Terms of Service apply.</i>
									</MixPanelTrackedLink>
								</i>
							</Typography>
						</Stack>
					</Grid>
					<Grid item xs={12} md={4} className="dright">
						<MediaQuery minDeviceWidth={992}>
							<ReactStickyBox offsetTop={20}>
								{pageDetails ? (
									<DonationImpactCard pageDetails={pageDetails} assignClasses="whitebox" isFundraiserPage={isFundraiserCheckout} />
								) : null}
							</ReactStickyBox>
						</MediaQuery>
					</Grid>
					<HBLForm />
				</Grid>
			</StyledCheckoutForm>
			<Footer />
		</React.Fragment>
	);
}
